import { ApiResponse, PaginatedResponse } from '../types';
import { decodeBase64 } from '../utils/helpers';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Get JWT token from Amplify session
const getAuthToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    return idToken || null;
  } catch (error) {
    console.error('Error fetching auth token:', error);
    return null;
  }
};

// Generic API response decoder
const decodeResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  try {
    const data = await response.json();

    // Check if response body is base64 encoded
    if (data.body && typeof data.body === 'string') {
      try {
        const decodedBody = decodeBase64(data.body);
        const parsedData = JSON.parse(decodedBody);
        return {
          success: data.statusCode === 200,
          data: parsedData,
          message: parsedData.message,
        };
      } catch (decodeError) {
        console.error('Error decoding response:', decodeError);
        return {
          success: false,
          error: 'Failed to decode API response',
        };
      }
    }

    // Handle error responses
    let errorMessage: string | undefined;
    if (!response.ok) {
      if (typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (typeof data.message === 'string') {
        errorMessage = data.message;
      } else if (data.error && typeof data.error.message === 'string') {
        errorMessage = data.error.message;
      } else if (data.data && typeof data.data.message === 'string') {
        errorMessage = data.data.message;
      } else {
        errorMessage = 'An error occurred';
      }
    }

    return {
      success: response.ok,
      data: data,
      error: errorMessage,
    };
  } catch (error) {
    console.error('Error processing response:', error);
    return {
      success: false,
      error: 'Failed to process API response',
    };
  }
};

// Generic API request handler
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header for /admin endpoints
    if (endpoint.startsWith('/admin')) {
      const token = await getAuthToken();
      if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn('No auth token available for admin endpoint');
      }
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    return await decodeResponse<T>(response);
  } catch (error) {
    console.error('API request error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error occurred',
    };
  }
};

// GET request
export const get = <T>(endpoint: string, headers?: HeadersInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, { method: 'GET', headers });
};

// POST request
export const post = <T>(
  endpoint: string,
  body: any,
  headers?: HeadersInit
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    headers,
  });
};

// PUT request
export const put = <T>(
  endpoint: string,
  body: any,
  headers?: HeadersInit
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers,
  });
};

// PATCH request
export const patch = <T>(
  endpoint: string,
  body: any,
  headers?: HeadersInit
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers,
  });
};

// DELETE request
export const del = <T>(endpoint: string, headers?: HeadersInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, { method: 'DELETE', headers });
};

export default {
  get,
  post,
  put,
  patch,
  del,
};
